import { getBaseThemeName, getConfig, getDocsConfig, getKbConfig } from "@/features/branding";
import { store } from "@/store";
import { CmsPost, internalApi } from "@/store/services/internal";
import { PostCategoryId, PostIds, PostSchema } from "./types";

/**
 * Retrieves a post from given data.
 * @param category - Category of post
 * @param id - ID of post
 * @returns - Post or undefined
 */
export const getPost = async (category: PostCategoryId, id: string): Promise<CmsPost | undefined> => {
  const slug = getPostId(category, id);

  return slug ? getPostBySlug(slug) : undefined;
};

/**
 * Retrieves a post via ID.
 * @param id - Post ID.
 * @returns - Post or undefined.
 */
export const getPostBySlug = async (id: string): Promise<CmsPost | undefined> => {
  const getAllPostsAction = internalApi.endpoints.getCmsPost.initiate(id);
  const { status, data } = await store.dispatch(getAllPostsAction);

  return status === "rejected" || !data ? undefined : data;
};

/**
 * Retrieves a post via ID and category.
 * @param id - Post ID.
 * @param category - Post ID.
 * @returns - Post or undefined.
 */
export const getPostByIdAndCategory = async (id: string, category: PostCategoryId): Promise<CmsPost | undefined> => {
  const categoryId = getPostId(category, id) || id;

  const cmsCategoryId =
    categoryId && typeof categoryId === "object" && "id" in categoryId
      ? (categoryId as Record<string, string>).id
      : categoryId;

  const getAllPostsAction = internalApi.endpoints.getCmsCategory.initiate(cmsCategoryId);
  const { status, data } = await store.dispatch(getAllPostsAction);

  return status === "rejected" || !data ? undefined : data;
};

export const getPostByKey = (items: Record<string, PostSchema> | undefined, key: string): PostSchema | undefined => {
  if (!items) return undefined;

  // in top level
  if (items[key]) {
    return items[key];
  }

  // nested
  for (const item of Object.values(items)) {
    if (item.items) {
      const found = getPostByKey(item.items, key);
      if (found) return found;
    }
  }

  return undefined;
};

/**
 * Finds the post ID for a given CMS post, if it exists.
 * @param category - Internal category (eg. "glossary").
 * @param id - Internal ID (eg. "rcps", "riskBands").
 * @returns - "Numerical" ID for HubSpot CMS Hub.
 */
export const getPostId = (category: PostCategoryId, id: string): string | undefined => {
  const { overrides } = getDocsConfig().contextualElementTutorials;

  // We either use the override or the default.
  const defaultData = getPostByKey(buildPostIds()[category], id);
  const overrideData = getPostByKey(overrides[category], id);

  const data = overrideData ?? defaultData;

  if (data === undefined) {
    return id;
  }

  return data.id;
};

export const getArticlesAndCategories = (
  items: Record<string, PostSchema> | undefined
): { articles: PostSchema[]; categories: PostSchema[] } => {
  if (!items) {
    return { articles: [], categories: [] };
  }

  return Object.values(items).reduce(
    (acc, item) => {
      if (item.type === "article") {
        return {
          articles: [...acc.articles, item],
          categories: acc.categories,
        };
      }

      if (item.type === "link") {
        return acc;
      }

      // text sections have no CMS post of their own, but their children can
      if (item.type === "text") {
        if (item.sectionType !== "grouped" || !item.items) {
          return acc;
        }

        const nested = getArticlesAndCategories(item.items);

        return {
          articles: [...acc.articles, ...nested.articles],
          categories: [...acc.categories, ...nested.categories],
        };
      }

      // category
      let nextArticles = acc.articles;
      let nextCategories = [...acc.categories, item];

      if (item.sectionType === "grouped" && item.items) {
        const nested = getArticlesAndCategories(item.items);
        nextArticles = [...nextArticles, ...nested.articles];
        nextCategories = [...nextCategories, ...nested.categories];
      }

      return {
        articles: nextArticles,
        categories: nextCategories,
      };
    },
    { articles: [] as PostSchema[], categories: [] as PostSchema[] }
  );
};
/**
 * Returns all CMS posts within a given category.
 */
export const getPostsByCategory = async (
  category: PostCategoryId
): Promise<{ posts: CmsPost[]; postSchemas: PostSchema[] } | undefined> => {
  const { overrides } = getDocsConfig().contextualElementTutorials;

  const categoryData = {
    ...buildPostIds()[category],
    ...(overrides[category] || []),
  } as Record<string, PostSchema>;

  const { articles, categories } = getArticlesAndCategories(categoryData);

  const data = await Promise.all([
    ...articles.map((article) => {
      const getAllPostsAction = internalApi.endpoints.getCmsPost.initiate(article.id);
      return store.dispatch(getAllPostsAction);
    }),
    ...categories.map((category) => {
      const getAllPostsAction = internalApi.endpoints.getCmsCategory.initiate(category.id);
      return store.dispatch(getAllPostsAction);
    }),
  ]);

  return {
    posts: data.reduce((list, { status, data }) => {
      return status === "rejected" || !data ? list : [...list, data];
    }, [] as CmsPost[]),
    postSchemas: [...articles, ...categories],
  };
};

/**
 * Returns all CMS posts within a given category.
 */
export const getNestedPostsByCategory = async (
  category: PostCategoryId,
  parentPostId: string
): Promise<{ posts: CmsPost[]; postSchemas: PostSchema[] } | undefined> => {
  const { overrides } = getDocsConfig().contextualElementTutorials;
  const parentPost = getPostByKey({ ...buildPostIds()[category], ...overrides[category] }, parentPostId);

  const { articles, categories } = getArticlesAndCategories(parentPost?.items);

  const data = await Promise.all([
    ...articles.map((article) => {
      const getAllPostsAction = internalApi.endpoints.getCmsPost.initiate(article.id);
      return store.dispatch(getAllPostsAction);
    }),
    ...categories.map((category) => {
      const getAllPostsAction = internalApi.endpoints.getCmsCategory.initiate(category.id);
      return store.dispatch(getAllPostsAction);
    }),
  ]);

  return {
    posts: data.reduce((list, { status, data }) => {
      return status === "rejected" || !data ? list : [...list, data];
    }, [] as CmsPost[]),
    postSchemas: [...articles, ...categories],
  };
};

/**
 *
 * @returns filtered out skipped posts
 */
const filterSkippedPosts = (posts: Record<string, any>, skips: string[] = []): Record<string, any> =>
  Object.keys(posts)
    .filter((key) => !skips.includes(key))
    .reduce((acc, key) => ({ ...acc, [key]: posts[key] }), {});

/**
 *
 * @returns filtered out skipped posts from nested items
 */
const filterSkippedNestedPosts = (posts: Record<string, any>, skips: string[]): Record<string, any> => {
  return Object.entries(filterSkippedPosts(posts, skips)).reduce((acc, [key, data]) => {
    if (data?.items) {
      data = { ...data, items: filterSkippedNestedPosts(data?.items, skips) };
    }
    return { ...acc, [key]: data };
  }, {});
};

/**
 * Builds a list of all post IDs.
 * @returns
 */
export const buildPostIds = (): PostIds => {
  const isHkma = getBaseThemeName() === "hkma";

  /** The ID of every tool currently active in the system. */
  const allToolIds = getConfig().featureSet.tools.map((tool) => tool.id);

  const postIds: PostIds = {
    glossary: {
      absoluteChange: { id: "54199018-487d-4eb4-92e3-38b31c21f519", type: "article" },
      address: { id: "0be62e47-933b-4596-977c-6f53d497bb50", type: "article" },
      adaptation: { id: "cf3e6dae-536f-4abd-bda8-acc13a8ca02e", type: "article" },
      anomalyManagementRp: { id: "01a97b66-6425-4b8f-bb52-ff8db9a1fad5", type: "article" },
      archetype: { id: "8e8a031f-7931-4488-ae7c-04f3f2d4888b", type: "article" },
      archetypesExplained: { id: "6206dd64-74d2-474e-a361-b8ca6390f5cd", type: "article" },
      assetUncertainty: { id: "72c2630e-417e-459a-ad6f-4255811cb249", type: "article" },
      autoEnhancedResilience: { id: "0192446d-0192-4727-b495-d0b221cb7b44", type: "article" },
      automatedResilience: { id: "01f0ec86-3c7e-4e1b-bf53-dbd64669f90e", type: "article" },
      avoidedRiskValue: { id: "d08c26c0-d56b-4eb3-b6b3-84f85f30b9b2", type: "article" },
      baselineAdaptation: { id: "ad7d71af-d55b-45f4-abe0-94371fd78eff", type: "article" },
      breakevenInvestment: { id: "41cc30ec-c4bf-49bf-8872-85d92a4a5090", type: "article" },
      buildYear: { id: "7dae882c-0571-443c-b1a9-6a418dfc21b5", type: "article" },
      climateHazards: { id: "e8e98ab8-7fc0-4b7a-ad8e-46a3826cc9b6", type: "article" },
      defences: { id: "5e60153c-bd51-4507-9dc2-797c8fd288c3", type: "article" },
      elevation: { id: "ceb3940a-7d66-437e-ae0b-9d20bc5a4d65", type: "article" },
      emissionScenarios: { id: "d69aebb6-fcb6-42e8-b8b0-7a58e64cb1ff", type: "article" },
      exposure: { id: "4398d7ec-6672-4125-847b-d405bcfde365", type: "article" },
      geocodeQuality: { id: "72a19945-06dc-4702-bde4-7f0117a45559", type: "article" },
      heatThreshold: { id: "b4db9b0f-0ef0-4f27-ab51-e15f135927fa", type: "article" },
      heightAboveGround: { id: "106dc9c2-1959-4600-afbb-ae51743ebe73", type: "article" },
      modernResAdaptation: { id: "df90f3cf-76d7-4d97-9cf8-25fa4433ceeb", type: "article" },
      latitude: { id: "d66912df-9b44-4dea-8925-98cae836ba21", type: "article" },
      longitude: { id: "bb0be531-4643-41c5-bb4b-26f20edf1a85", type: "article" },
      lowResAdaptation: { id: "a0275cb1-e2b7-4271-8c7c-1eb669414e30", type: "article" },
      marketValue: { id: "9d8e90d7-b1cc-4fcd-9d8b-07ac1c2620e0", type: "article" },
      material: { id: "d1e1114b-3c1d-4495-9dac-3866f878b5e3", type: "article" },
      metadata: { id: "469b23f8-14c0-4e84-a695-4b030588f0e1", type: "article" },
      metrics: { id: "353f6ef0-307b-4568-8781-9ce65437b954", type: "article" },
      mortgageRate: { id: "7e194e71-37e5-4fdc-a676-68dbc88ceaf4", type: "article" },
      rcp: { id: "de298320-5632-4bf2-b3f2-c09dbea93458", type: "article" },
      relativeChange: { id: "c8ff6be9-3058-4ee7-9efa-813e222289d2", type: "article" },
      replacementCost: { id: "35645da9-f6b6-477b-9223-98b23b2f5d72", type: "article" },
      returnFrequency: { id: "e7480e7f-24a0-4f0b-b8b4-33f9f4b9650b", type: "article" },
      returnPeriod: { id: "d4c020f4-7add-481f-bfc4-c18fc3a2417b", type: "article" },
      riskBands: { id: "0b4b196a-5683-4b88-9b64-cf742ae4aefd", type: "article" },
      riskiestPoint: { id: "5039b723-f65c-4f16-9876-d911678cd7e8", type: "article" },
      significantHazards: { id: "34443e0d-48de-40a4-bfc2-2283fbae652a", type: "article" },
      simpleRiskRatings: { id: "22136c34-1fa8-43cd-9284-b31eb3259590", type: "article" },
      ssp: { id: "45fb9e60-bec4-44ee-8929-1ef02570ad8e", type: "article" },
      structuralAnalysis: { id: "6e7e1a80-7f87-416b-8981-d4627e92c7c9", type: "article" },
      tcwThreshold: { id: "6fb3dd29-e3a4-4ba0-a238-6e9c1de4280c", type: "article" },
      valueProtected: { id: "3ad908ed-ccc5-47cf-9799-e045b140cde5", type: "article" },
      windThreshold: { id: "c7b43dfb-9272-4bd4-9fe8-f7ea9e4b4962", type: "article" },
      fireProtection: { id: "6d099c98-f8de-4801-8a41-2d4b98c0074c", type: "article" },
      foundationDesign: { id: "3d282b97-21f6-4769-a2bd-f6e6bc1895fe", type: "article" },
    },
    guide: {
      otherPlatformInfo: { id: "2b2e7ee3-19e5-4ad4-b621-269eee97f0fe", type: "category" },
      toolDescriptions: {
        id: "94672b8e-eb07-4d76-afb0-9279abd0ea4b",
        type: "category",
        sectionType: "grouped",
        items: {
          single: { id: "9af50f4e-cef5-4e89-832a-efdcf6a75033", type: "article" },
          batch: { id: "55dc2d3e-0402-4ba8-9a58-ea63e19b1e93", type: "article" },
          portfolioInsights: { id: "1c2af293-3bff-488e-8e3b-703736a1bacd", type: "article" },
          largeSite: { id: "d746e30c-a7df-4707-8d09-a892ae3d618e", type: "article" },
        },
      },
      tutorials: {
        id: "90eda7c9-2e37-45cd-9a39-b4d4b36933a8",
        type: "category",
        sectionType: "grouped",
        items: {
          largeSite: {
            id: "largeSite",
            type: "link",
            sectionType: "direct",
            link: { url: "/tools/comprehensive/large-site-analysis?tutorial", title: "Large Site Analysis Tutorial" },
          },
          portfolioInsights: {
            id: "portfolioInsights",
            type: "link",
            sectionType: "direct",
            link: { url: "/tools/comprehensive/portfolio-analysis?tutorial", title: "Portfolio Analysis Tutorial" },
          },
          batch: {
            id: "batch",
            type: "link",
            sectionType: "direct",
            link: { url: "/tools/screening/batch?tutorial", title: "Quick Batch Screen Tutorial" },
          },
          single: {
            id: "single",
            type: "link",
            sectionType: "direct",
            link: { url: "/tools/screening/single?tutorial", title: "Single Asset Screen Tutorial" },
          },
        },
      },
    },
    faqs: {
      automatedResilienceFaqs: {
        id: "ff65126e-e0d4-42c4-bd14-84211c24d6da",
        type: "category",
        sectionType: "grouped",
        items: {
          general: { id: "175a67fd-2e00-4c92-80a9-ce82251e3181", type: "article" },
          pathwaySettings: { id: "0ee543b0-dcd4-41b9-b027-0e27186ed74a", type: "article" },
          understandingResults: { id: "515e0ed1-556f-46d6-bbd0-2940445c15aa", type: "article" },
        },
      },
      dataSecurity: {
        id: "data-security",
        sectionType: "grouped",
        type: "text",
        title: "Data Security",
        items: {
          encryptionKeys: {
            id: "1bfe61ef-ca12-4b06-90e3-79fd8e8cf0b1",
            type: "article",
          },
        },
      },
      general: {
        id: "general",
        sectionType: "grouped",
        type: "text",
        title: "Other FAQs",
        items: {
          general: {
            id: "general",
            type: "link",
            sectionType: "direct",
            link: {
              url: "https://learn.xdi.systems/docs/frequently-asked-questions",
              title: "FAQs",
              isExternal: true,
            },
          },
        },
      },
    },
    method: {
      structuralAnalysis: { id: "5d68143d-e0ef-40ae-ae15-f9a7665340ae", type: "category" },
      geocoding: { id: "db825135-ec59-4332-a598-8c68de678109", type: "article" },
      archetypesExplained: { id: "6206dd64-74d2-474e-a361-b8ca6390f5cd", type: "article" },
      automatedResilience: { id: "01f0ec86-3c7e-4e1b-bf53-dbd64669f90e", type: "article" },
      climateHazards: {
        id: "28fa8de7-da40-42e2-a222-7f9a4765a35e",
        type: "category",
        sectionType: "nested",
        items: {
          coastalInnundiation: { id: "023dffb5-c833-456e-bbe0-8fafede975bf", type: "article" },
          connectiveInnundiation: { id: "92a5bf9f-21ce-41c3-858d-5ccd5ad5745c", type: "article" },
          extremeHeat: { id: "0f12195f-7ec1-49f3-8b62-d026b5f2e580", type: "article" },
          extremeWind: { id: "931eaba2-53d3-443f-b5fd-4c1c7fa6b213", type: "article" },
          forestFire: { id: "45be3511-901f-4ce3-a3ea-dd61893b96f4", type: "article" },
          freezeThaw: { id: "3f05a743-6a76-42e7-b096-c07f6319a14f", type: "article" },
          landslide: { id: "ee3540d3-a0fe-495e-b507-15419e0149e7", type: "article" },
          riverineFlood: { id: "7b677898-fbaa-4450-829d-65d0bd7df86f", type: "article" },
          soilMovement: { id: "a3fd35b4-d3e1-4973-b437-2652580a1c46", type: "article" },
          surfaceWaterFlood: { id: "8dc88ab5-1d7c-4600-a126-d6eb9d337ae1", type: "article" },
          tcss: { id: "71a03ef2-2c41-48cf-aa22-9324b0e60a47", type: "article" },
          tcw: { id: "4ec77b68-5015-4eda-87e9-92bab42a2798", type: "article" },
        },
      },
      analysisSettingsAndInputs: {
        id: "b4169258-9145-469a-99fe-52839d76a705",
        type: "category",
        sectionType: "nested",
        items: {
          address: { id: "9e283032-47d3-4ed9-93cd-1e4ebc54413c", type: "article" },
          anomalyManagement: { id: "87e9ed39-e3bc-49e2-ac74-600e902870a9", type: "article" },
          archetype: { id: "9cbfd977-9b8c-4346-bcc5-92613bb44ed4", type: "article" },
          buildYear: { id: "5841c252-ec3d-4065-96b9-56847a7e71c3", type: "article" },
          defences: { id: "412808d1-3586-4123-adb6-66709aa94b5e", type: "article" },
          elevation: { id: "2bf4a57b-ab31-49c1-b0a4-76011ffaf6cb", type: "article" },
          extremeWindThreshold: { id: "da081057-2acd-40b4-a967-895f4ca80ae9", type: "article" },
          heatThreshold: { id: "ee2e6e86-f55a-477b-a124-426cc1360545", type: "article" },
          heightAboveGround: { id: "86decc62-99f4-4661-8946-0b1273122ef7", type: "article" },
          latitude: { id: "cf605642-1ae8-456e-99f5-751e6d520734", type: "article" },
          longitude: { id: "03823d72-689d-4f7c-87d1-c62e4cffa57d", type: "article" },
          marketValue: { id: "927de2b1-3e48-4f23-b2ab-4af0bda66a73", type: "article" },
          mortgageRate: { id: "6ffda4da-d6b0-4bb3-924c-faa8a448dca5", type: "article" },
          replacementCost: { id: "120bc508-df89-40b5-822f-7a2d09a25ce5", type: "article" },
          tcwThreshold: { id: "6fb3dd29-e3a4-4ba0-a238-6e9c1de4280c", type: "article" },
          fireProtection: { id: "53c40c7b-95b3-4966-98dd-c6341923939b", type: "article" },
          foundationDesign: { id: "94a8ff25-a60c-4faf-a7a6-15503189658a", type: "article" },
        },
      },
      riskMetricsAndOutputs: {
        id: "06a994a2-73c9-4529-b25a-bf4f6f20fa9b",
        type: "category",
        sectionType: "nested",
        items: {
          cav: { id: "f71a6100-3d23-4d93-a5d5-ddb8c1f285d4", type: "article" },
          floodExposure: { id: "9fe9a207-58a1-4766-8b08-c2eccc063909", type: "article" },
          fp: { id: "9ead9d10-d854-4ff5-9d50-581fedef232b", type: "article" },
          metadata: { id: "60a8b8cf-1822-44cd-8aac-f3fd143ee23b", type: "article" },
          mvar: { id: "3611dd98-43ee-4892-839a-7e0603385ef7", type: "article" },
          pl: { id: "7766e86f-f56a-4feb-9c63-9c2ce4233699", type: "article" },
          riskBands: { id: "f26ace4b-f5f5-494a-9a46-7a8d1d25a41a", type: "article" },
          riskiestPoint: { id: "71db225e-1398-4c31-be26-c919d861fa2c", type: "article" },
          significantHazards: { id: "cba4d0bb-c27e-4bc3-b7d7-75d4d38aa42f", type: "article" },
          simpleRiskRatings: { id: "19a745e8-96ab-43b5-bce4-6bfaf421254d", type: "article" },
          tip: { id: "1bb99da6-f039-40d3-9d91-3285caa829cd", type: "article" },
          ttip: { id: "c737a3c2-4e2a-4e3c-be6a-f3d9d9c31ae2", type: "article" },
          var: { id: "d92cb09d-0d01-4a9b-8f24-994cc082a3ed", type: "article" },
        },
      },
      // "temporarily" disabled while hkma review
      // geocodingExplanation: "geocoding-explanation",
      // ngfs: "ngfs",
    },
    test: {
      testPageRegular: { id: "test-page-regular", type: "article" },
      testPageWide: { id: "test-page-wide", type: "article" },
    },
  };

  if (isHkma) {
    postIds.guide.enteringLocationsAndAddresses = { id: "entering-locations-and-addresses-hk", type: "article" };
    postIds.guide.tutorials = {
      ...postIds.guide.tutorials,
      items: {
        ...postIds.guide.tutorials?.items,
        trainingWorkshop: { id: "b0f8d4d7-6d26-4e1c-970a-154ec1f2b2a1", type: "article" },
      },
    };
    postIds.method = {
      archetypesReports: { id: "60a23777-e2a5-4a77-a2ba-594a9d3f66ff", type: "article" },
      geocoding: {
        id: "geocoding",
        type: "link",
        sectionType: "direct",
        link: {
          url: getKbConfig().geocoding.publicDocsUrl || "/geocoding",
          title: "Geocoding",
          isExternal: true,
        },
      },
      methodology: { id: "1ee7809c-945a-44d2-be0e-ff61cb965fbb", type: "article" },
      modellingUpdate: { id: "cd33a6b4-4927-4505-93c0-e61f2054dc7b", type: "article" },
    };

    const skipInHkma: Record<PostCategoryId, string[]> = {
      glossary: [
        "method",
        "absoluteChange",
        "adaptation",
        "assetUncertainty",
        "autoEnhancedResilience",
        "automatedResilience",
        "avoidedRiskValue",
        "baselineAdaptation",
        "breakevenInvestment",
        "modernResAdaptation",
        "lowResAdaptation",
        "relativeChange",
        "valueProtected",
      ],
      method: ["automatedResilience"],
      guide: ["otherPlatformInfo", "largeSite"],
      test: [],
      faqs: ["general", "dataSecurity", "automatedResilienceFaqs"],
    };

    Object.keys(skipInHkma).forEach((categoryKey: string) => {
      const category = categoryKey as PostCategoryId;
      postIds[category] = filterSkippedNestedPosts(postIds[category], skipInHkma[category]);
    });
  }

  if (allToolIds.indexOf("portfolioAnalysis") !== -1) {
    postIds.glossary.portfolioAnalysisResultsExplanation = {
      id: "what-do-my-portfolio-analysis-results-mean",
      type: "article",
    };
  }

  return postIds;
};
